import classNames from 'classnames';
import jsCookie from 'js-cookie';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

// components
import {
    BodyContainerStyled,
    GridContainerStyled,
    ViewReceiptButtonStyled,
    OrderReceiptContainerStyled,
    DividerBlueStyled,
    NwpCtaContainerStyled,
} from './index.styled';

import NwpCtaPanel from './NwpCtaPanel';
import OrderReceipt from './OrderReceipt';
import BulletListIcon from '@icons/BulletListIcon';
import { BodyMedium, H3 } from '@styled';

// functionality
import { getCartById } from '@general/api/cartAPI';
import productTypes from '@general/productTypes';
import reactQueryClient from '@general/reactQueryClient';
import useCartItemsGroupedByMember from '@hooks/useCartItemsGroupedByMember';
import { useScreenMinMd, useScreenSmDown } from '@hooks/useResponsive';
import useViewer from '@hooks/useViewer';
import useIcscTracking from '@react/tracking/useIcscTracking';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import { getLoginPath } from '../../../../helpers/url.helpers';

const CartConfirmationPage = () => {
    // -----------------------------
    // State
    // -----------------------------

    const [cartId, setCartId] = useState();
    const [cart, setCart] = useState();
    const [isReceiptVisible, setIsReceiptVisible] = useState(false);

    // -----------------------------
    // Hooks
    // -----------------------------

    const history = useHistory();
    const { Track } = useIcscTracking({
        app_area: 'Cart Confirmation',
    });

    const { viewer } = useViewer();
    const { cart: queriedCart } = useCartFromAPI({ viewerId: viewer?.id });
    const { itemsGroupedByMember } = useCartItemsGroupedByMember({
        cart,
        itemCount: cart ? cart.items.length : null,
        total: cart?.total,
    });

    const isScreenMinMd = useScreenMinMd();
    const isScreenSmDown = useScreenSmDown();

    // -----------------------------
    // Effects
    // -----------------------------

    useEffect(() => {
        if (!viewer) {
            return;
        }

        if (queriedCart) {
            setCart(queriedCart);
            return;
        }

        const cookie = jsCookie.get('cart_id_completed');
        if (cookie) {
            setCartId(cookie || null);
        } else {
            history.push('/cart/view');
        }
    }, [viewer, history, queriedCart]);

    useEffect(() => {
        if (!cartId) {
            return;
        }

        (async () => {
            const cartResponse = await getCartById({ cartId });
            setCart(cartResponse);
        })();
    }, [cartId]);

    // -----------------------------
    // Functions
    // -----------------------------

    const getTextForPurchaseType = ({ memberId, memberName, itemType, item }) => {
        const isSelf = memberId === viewer?.recordId;

        if (isSelf && itemType === productTypes.MEMBERSHIP) {
            return 'Your ICSC Membership is now active!';
        }

        if (isSelf && itemType === productTypes.EVENT) {
            return `You are now registered for ${item.name}!`;
        }

        if (!isSelf && itemType === productTypes.MEMBERSHIP) {
            return `${memberName}'s ICSC Membership is now active!`;
        }

        if (!isSelf && itemType === productTypes.EVENT) {
            return `${memberName} is now registered for ${item.name}!`;
        }
    };

    const getObscuredEmail = () => {
        const email = viewer?.email;
        const emailParts = email.split('@');
        const firstPart = emailParts[0];
        const lastPart = emailParts[1];

        let firstPartObscured;

        if (firstPart.length <= 3) {
            firstPartObscured = '*'.repeat(firstPart.length);
        } else {
            const last3Characters = firstPart.slice(-3);
            firstPartObscured = `${'*'.repeat(firstPart.length - 3)}${last3Characters}`;
        }

        return `${firstPartObscured}@${lastPart}`;
    };

    // -----------------------------
    // Render
    // -----------------------------

    if (viewer === null) {
        window.location.href = getLoginPath();
    }

    if (!itemsGroupedByMember) {
        return null;
    }

    return (
        <Track>
            <BodyContainerStyled className="container">
                <GridContainerStyled>
                    <div className="col-span-5">
                        <H3
                            className={classNames({
                                'mb-0.25rem': isScreenMinMd,
                                'mb-1.25rem': isScreenSmDown,
                            })}
                        >
                            Thank you!
                        </H3>

                        {itemsGroupedByMember.map(itemsForMember => (
                            <React.Fragment key={itemsForMember.memberId}>
                                {itemsForMember.itemsGroupedByType.map(itemsForType => (
                                    <React.Fragment key={itemsForType.type}>
                                        {itemsForType.items.map(item => (
                                            <BodyMedium key={item.product_id}>
                                                {getTextForPurchaseType({
                                                    memberId: itemsForMember.memberId,
                                                    memberName: itemsForMember.memberName,
                                                    item,
                                                    itemType: itemsForType.type,
                                                })}
                                            </BodyMedium>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </React.Fragment>
                        ))}

                        <div className="mt-1rem">
                            <BodyMedium>
                                We sent an email to {viewer?.email} with your order confirmation and receipt. If the
                                email hasn't arrived momentarily, please check your spam folder to see if the email was
                                routed there.
                            </BodyMedium>
                        </div>

                        {!isReceiptVisible && (
                            <div
                                className={classNames({
                                    'mt-2.5rem': isScreenMinMd,
                                    'mt-2rem': isScreenSmDown,
                                })}
                            >
                                <ViewReceiptButtonStyled onClick={() => setIsReceiptVisible(true)}>
                                    <BulletListIcon />
                                    <span className="ml-0.625rem">View Order Receipt</span>
                                </ViewReceiptButtonStyled>
                            </div>
                        )}

                        <OrderReceiptContainerStyled visible={isReceiptVisible}>
                            <DividerBlueStyled className="mb-0.75rem" />
                            <OrderReceipt cart={cart} />
                        </OrderReceiptContainerStyled>
                    </div>
                </GridContainerStyled>

                {isScreenMinMd && (
                    <NwpCtaContainerStyled>
                        <NwpCtaPanel />
                    </NwpCtaContainerStyled>
                )}
            </BodyContainerStyled>

            {isScreenSmDown && (
                <NwpCtaContainerStyled>
                    <NwpCtaPanel />
                </NwpCtaContainerStyled>
            )}
        </Track>
    );
};

export default CartConfirmationPage;
